import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useGuardedNavigate } from "./NavigationGuard";
import {
  SignOut, DownloadSimple, CaretDown, Megaphone, Users, ChartBar, Briefcase,
  Receipt, ShieldCheck, ChartLineUp, Wrench, Tag, Truck,
  IdentificationCard, ShoppingCart, ShareNetwork, MagnifyingGlass, X,
} from "@phosphor-icons/react";
import {
  ROLE_ACCESS,
  ALL_DASHBOARD_ITEMS, ALL_MARKETING_ITEMS, ALL_DIGITAL_MARKETING_ITEMS,
  ALL_CRM_ITEMS, ALL_PRODUCTS_ITEMS, ALL_FINANCE_ITEMS,
  ALL_PURCHASE_ITEMS, ALL_STORE_ITEMS, ALL_OPERATIONS_ITEMS,
  ALL_HR_ITEMS, ALL_WORKSPACE_ITEMS, ALL_ADMIN_ITEMS,
  ROLE_BADGE_COLORS,
  navItemAllowedByPerms,
} from "../config/navConfig";
import { getRoleBadgeLabel } from "../config/uiHelpers";
import { filterSections } from "../utils/navSearch";
import GlobalNavSearch from "./GlobalNavSearch";

const REPORTS_HUB_NAV_ITEM = { path: '/reports-hub', key: 'reports-hub', label: 'Reports Hub', icon: ChartLineUp, testId: 'nav-reports-hub' };
const REPORTS_NAV_ITEM = { path: '/reports', key: 'reports', label: 'Export Reports', icon: DownloadSimple, testId: 'nav-reports' };
const COLLAPSE_KEY = "swiftech-sidebar-collapse-v2";

// Active-match for items that can include `?…` deep links.
const matchActive = (item, pathname, search) => {
  if (item.path.includes('?')) {
    return (pathname + search) === item.path;
  }
  // Plain landing path is active only when no query string is present
  return pathname === item.path && !search;
};

/* ---------- Leaf nav item ---------- */
const NavItem = ({ item, active, navigate }) => {
  const Icon = item.icon;
  return (
    <a
      href={item.path}
      data-testid={item.testId}
      onClick={(event) => {
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          event.button !== 0
        ) {
          return;
        }
        event.preventDefault();
        navigate(item.path);
      }}
      className={`w-full flex items-center gap-2.5 pl-8 pr-3 py-1.5 rounded-none transition-colors duration-150 ${
        active ? 'bg-[#002FA7] text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
      }`}
    >
      <Icon size={14} weight={active ? 'fill' : 'regular'} />
      <span className="font-medium text-xs truncate">{item.label}</span>
    </a>
  );
};

/* ---------- Collapsible section header ---------- */
const Section = ({ id, label, icon: Icon, items, pathname, search, navigate, open, onToggle, forceOpen = false }) => {
  if (!items || items.length === 0) return null;
  const containsActive = items.some(it => matchActive(it, pathname, search));
  // Display rules:
  //  - `forceOpen` (search active + section has matches) overrides everything
  //  - else `open` strictly controls expansion. We intentionally DROP the old
  //    `|| containsActive` so a user can collapse the active module by clicking it.
  const expanded = forceOpen || open;
  return (
    <div className="mb-0.5">
      <button
        data-testid={`sidebar-section-${id}`}
        onClick={() => onToggle(id)}
        className={`w-full flex items-center justify-between px-3 py-1.5 group transition-colors ${
          containsActive ? 'bg-slate-800/60 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
        }`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Icon size={14} weight={containsActive ? 'fill' : 'regular'} />
          <span className="text-[11px] uppercase tracking-[0.12em] font-bold">{label}</span>
        </span>
        <span className="flex items-center gap-1.5 shrink-0">
          <span className="text-[9px] text-slate-500 group-hover:text-slate-400 tabular-nums">{items.length}</span>
          <CaretDown size={10} weight="bold" className={`transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`} />
        </span>
      </button>
      <div className={`overflow-hidden transition-[max-height] duration-200 ${expanded ? 'max-h-[1200px]' : 'max-h-0'}`}>
        <div className="py-0.5">
          {items.map(item => (
            <NavItem key={item.key} item={item} active={matchActive(item, pathname, search)} navigate={navigate} />
          ))}
        </div>
      </div>
    </div>
  );
};

const Sidebar = ({ user, logout }) => {
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const role = user?.role || 'customer';
  const access = ROLE_ACCESS[role] || ROLE_ACCESS.customer;

  // Phase B.5 — Permission-based filtering, layered ON TOP of ROLE_ACCESS (intersection).
  // Behaviour:
  //  - Admin: bypass perm filter (always sees everything ROLE_ACCESS grants).
  //  - Customer: keep ROLE_ACCESS behaviour (single dashboard).
  //  - Other roles with perm_keys: intersection of ROLE_ACCESS ∩ NAV_PERMISSION_MAP.
  //  - Other roles WITHOUT perm_keys (legacy session): pure ROLE_ACCESS, no perm filter.
  //  - Failure protection: if the intersection wipes the entire sidebar, fall back to ROLE_ACCESS.
  const hasPermKeys = Array.isArray(user?.perm_keys) && user.perm_keys.length > 0;
  const usePermFilter = role !== 'admin' && role !== 'customer' && hasPermKeys;
  const passPerm = (item) => !usePermFilter || navItemAllowedByPerms(item.key, user);

  // ROLE_ACCESS-only filter (legacy)
  const legacyDash    = ALL_DASHBOARD_ITEMS.filter(i => (access.dashboards || []).includes(i.key));
  const legacyMkt     = ALL_MARKETING_ITEMS.filter(i => (access.marketing || []).includes(i.key));
  const legacyDig     = ALL_DIGITAL_MARKETING_ITEMS.filter(i => (access.digital_marketing || []).includes(i.key));
  const legacyCrm     = ALL_CRM_ITEMS.filter(i => (access.crm || []).includes(i.key));
  const legacyProd    = ALL_PRODUCTS_ITEMS.filter(i => (access.products || []).includes(i.key));
  const legacyFin     = ALL_FINANCE_ITEMS.filter(i => (access.finance || []).includes(i.key));
  const legacyPur     = ALL_PURCHASE_ITEMS.filter(i => (access.purchase || []).includes(i.key));
  const legacyStr     = ALL_STORE_ITEMS.filter(i => (access.store || []).includes(i.key));
  const legacyOps     = ALL_OPERATIONS_ITEMS.filter(i => (access.operations || []).includes(i.key));
  const legacyHr      = ALL_HR_ITEMS.filter(i => (access.hr || []).includes(i.key));
  const legacyWs      = ALL_WORKSPACE_ITEMS.filter(i => (access.workspace || []).includes(i.key));
  const legacyAdm     = ALL_ADMIN_ITEMS.filter(i => (access.admin || []).includes(i.key));

  // Compute permission-intersected lists
  const permDash    = usePermFilter ? legacyDash.filter(passPerm) : legacyDash;
  const permMkt     = usePermFilter ? legacyMkt.filter(passPerm)  : legacyMkt;
  const permDig     = usePermFilter ? legacyDig.filter(passPerm)  : legacyDig;
  const permCrm     = usePermFilter ? legacyCrm.filter(passPerm)  : legacyCrm;
  const permProd    = usePermFilter ? legacyProd.filter(passPerm) : legacyProd;
  const permFin     = usePermFilter ? legacyFin.filter(passPerm)  : legacyFin;
  const permPur     = usePermFilter ? legacyPur.filter(passPerm)  : legacyPur;
  const permStr     = usePermFilter ? legacyStr.filter(passPerm)  : legacyStr;
  const permOps     = usePermFilter ? legacyOps.filter(passPerm)  : legacyOps;
  const permHr      = usePermFilter ? legacyHr.filter(passPerm)   : legacyHr;
  const permWs      = usePermFilter ? legacyWs.filter(passPerm)   : legacyWs;
  const permAdm     = usePermFilter ? legacyAdm.filter(passPerm)  : legacyAdm;

  const totalAfterPerm = permDash.length + permMkt.length + permDig.length + permCrm.length
    + permProd.length + permFin.length + permPur.length + permStr.length + permOps.length
    + permHr.length + permWs.length + permAdm.length;
  const totalLegacy = legacyDash.length + legacyMkt.length + legacyDig.length + legacyCrm.length
    + legacyProd.length + legacyFin.length + legacyPur.length + legacyStr.length + legacyOps.length
    + legacyHr.length + legacyWs.length + legacyAdm.length;

  // Failure protection — sidebar collapsed to nothing → revert to ROLE_ACCESS
  const useFallback = usePermFilter && totalAfterPerm === 0 && totalLegacy > 0;
  const dashboardItems = useFallback ? legacyDash : permDash;
  const marketingItems = useFallback ? legacyMkt  : permMkt;
  const digitalItems   = useFallback ? legacyDig  : permDig;
  const crmItems       = useFallback ? legacyCrm  : permCrm;
  const productsItems  = useFallback ? legacyProd : permProd;
  const financeItems   = useFallback ? legacyFin  : permFin;
  const purchaseItems  = useFallback ? legacyPur  : permPur;
  const storeItems     = useFallback ? legacyStr  : permStr;
  const operationsItems= useFallback ? legacyOps  : permOps;
  const hrItems        = useFallback ? legacyHr   : permHr;
  const workspaceItems = useFallback ? legacyWs   : permWs;
  const adminItems     = useFallback ? legacyAdm  : permAdm;

  if (useFallback && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('[Sidebar] Permission filter wiped sidebar — falling back to ROLE_ACCESS for role', role);
  }

  const badgeColor = ROLE_BADGE_COLORS[role] || ROLE_BADGE_COLORS.customer;
  const pathname = location.pathname;
  const search = location.search;

  // Determine which section owns the active route (auto-open it)
  const sectionOf = useMemo(() => {
    const lookup = [
      ['dashboards', dashboardItems],
      ['marketing', marketingItems],
      ['digital', digitalItems],
      ['crm', crmItems],
      ['products', productsItems],
      ['finance', financeItems],
      ['purchase', purchaseItems],
      ['store', storeItems],
      ['operations', operationsItems],
      ['hr', hrItems],
      ['workspace', workspaceItems],
      ['admin', adminItems],
    ];
    for (const [name, items] of lookup) {
      if (items.some(it => matchActive(it, pathname, search))) return name;
    }
    if (pathname === '/reports' || pathname === '/reports-hub' || pathname.startsWith('/reports/')) return 'reports';
    return 'dashboards';
  }, [pathname, search, dashboardItems, marketingItems, digitalItems, crmItems, productsItems, financeItems, purchaseItems, storeItems, operationsItems, hrItems, workspaceItems, adminItems]);

  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = localStorage.getItem(COLLAPSE_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return { [sectionOf]: true };
  });

  useEffect(() => {
    if (sectionOf && !openSections[sectionOf]) {
      setOpenSections(prev => ({ ...prev, [sectionOf]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionOf]);

  const toggle = (key) => {
    // Option A behavior: clicking a section either opens it (closing all others)
    // OR closes it if it was already open. This makes "collapse same module by
    // clicking it again" work even when the section contains the active route.
    setOpenSections(prev => {
      const wasOpen = !!prev[key];
      const next = wasOpen ? {} : { [key]: true };
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const expandAll = () => {
    const all = { dashboards: true, marketing: true, digital: true, crm: true, products: true, finance: true, purchase: true, store: true, operations: true, hr: true, workspace: true, admin: true, reports: true };
    setOpenSections(all);
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(all)); } catch { /* ignore */ }
  };
  const collapseAll = () => {
    setOpenSections({});
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify({})); } catch { /* ignore */ }
  };

  const reportsActive = pathname === '/reports' || pathname === '/reports-hub' || pathname.startsWith('/reports/');

  // Phase Sidebar-UX-1 — local search + Ctrl/Cmd+K global palette
  const [searchQuery, setSearchQuery] = useState("");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const isSearching = searchQuery.trim().length > 0;

  // Build the role-filtered "sections" tuple list once. This is the SINGLE source
  // of truth shared by both the sidebar (with filtering & force-expand) and the
  // Ctrl+K palette (flattened list). Reports tab is included as its own section.
  const reportsSectionItems = useMemo(() => {
    if (!access.reports) return [];
    return [REPORTS_HUB_NAV_ITEM, REPORTS_NAV_ITEM];
  }, [access.reports]);

  const sections = useMemo(() => [
    ['dashboards', 'Dashboards',           dashboardItems, ChartBar],
    ['marketing',  'Marketing & Leads',    marketingItems, Megaphone],
    ['digital',    'Digital Marketing',    digitalItems, ShareNetwork],
    ['crm',        'CRM & Sales',          crmItems, Users],
    ['products',   'Products & Catalogue', productsItems, Tag],
    ['finance',    'Finance',              financeItems, Receipt],
    ['purchase',   'Purchase',             purchaseItems, ShoppingCart],
    ['store',      'Store & Delivery',     storeItems, Truck],
    ['operations', 'Operations / Service', operationsItems, Wrench],
    ['hr',         'HR & Team',            hrItems, IdentificationCard],
    ['workspace',  'Workspace',            workspaceItems, Briefcase],
    ['admin',      'Admin Settings',       adminItems, ShieldCheck],
    ['reports',    'Reports',              reportsSectionItems, ChartBar],
  ], [dashboardItems, marketingItems, digitalItems, crmItems, productsItems, financeItems, purchaseItems, storeItems, operationsItems, hrItems, workspaceItems, adminItems, reportsSectionItems]);

  // Apply the search filter once and reuse for rendering.
  const filteredSections = useMemo(() => filterSections(sections, searchQuery), [sections, searchQuery]);
  const filteredById = useMemo(() => {
    const map = {};
    filteredSections.forEach(([id, , items]) => { map[id] = items; });
    return map;
  }, [filteredSections]);

  // While searching, ALL matching sections must auto-expand (so children are visible).
  const isSectionForceOpen = (id) => isSearching && (filteredById[id]?.length || 0) > 0;
  // Helper: which items survive the filter for a specific section, with fallback
  // to the full role-filtered list when not searching.
  const getDisplayItems = (id, fullItems) => isSearching ? (filteredById[id] || []) : fullItems;
  // When searching and a section is wiped, hide its header entirely
  const showSection = (id, fullItems) => {
    const items = getDisplayItems(id, fullItems);
    return items.length > 0;
  };

  // Ctrl/Cmd+K → open global palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="fixed top-0 left-0 h-screen w-64 bg-slate-900 text-white flex flex-col z-50">
      {/* Brand */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center gap-2 mb-2">
          <img src="/assets/swiftech-logo.jpg" alt="SwifTech India"
            data-testid="sidebar-logo" className="w-8 h-8 object-contain bg-white rounded-sm p-0.5" />
          <span className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>SwifTech India</span>
        </div>
        <p className="text-xs text-slate-400 truncate">{user?.name}</p>
        <span className={`inline-block mt-1 rounded-none px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${badgeColor}`}>
          {getRoleBadgeLabel(role)}
        </span>
      </div>

      {/* Search box + Ctrl+K button */}
      <div className="px-3 py-2 border-b border-slate-800">
        <div className="relative">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="sidebar-search-input"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search menu…"
            className="w-full bg-slate-800 text-white text-xs pl-8 pr-16 py-1.5 rounded-sm placeholder:text-slate-500 outline-none focus:bg-slate-700 transition-colors"
          />
          {searchQuery ? (
            <button
              data-testid="sidebar-search-clear"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              aria-label="Clear search"
            >
              <X size={12} weight="bold" />
            </button>
          ) : (
            <button
              data-testid="sidebar-open-palette"
              onClick={() => setPaletteOpen(true)}
              title="Open global search (Ctrl/Cmd + K)"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-slate-700 hover:bg-slate-600 hover:text-white rounded-sm"
            >
              ⌘K
            </button>
          )}
        </div>
      </div>

      {/* Expand / Collapse controls */}
      <div className="px-2 py-1 border-b border-slate-800 flex items-center justify-between text-[10px] text-slate-500">
        <span className="px-2 font-bold uppercase tracking-wider">Menu</span>
        <div className="flex items-center gap-0.5">
          <button onClick={expandAll} data-testid="sidebar-expand-all" title="Expand all"
            className="px-1.5 py-0.5 hover:text-slate-200 hover:bg-slate-800 uppercase tracking-wider font-bold">Open</button>
          <span className="text-slate-700">|</span>
          <button onClick={collapseAll} data-testid="sidebar-collapse-all" title="Collapse all"
            className="px-1.5 py-0.5 hover:text-slate-200 hover:bg-slate-800 uppercase tracking-wider font-bold">Close</button>
        </div>
      </div>

      {/* Nav — department-wise; middle area scrolls independently between fixed header and fixed logout */}
      <nav className="flex-1 py-2 overflow-y-auto" data-testid="sidebar-nav">
        {isSearching && filteredSections.length === 0 && (
          <div data-testid="sidebar-search-empty" className="px-3 py-6 text-center text-xs text-slate-500">
            No menu found for “{searchQuery.trim()}”
          </div>
        )}
        {showSection('dashboards', dashboardItems) && (
          <Section id="dashboards" label="Dashboards"        icon={ChartBar}      items={getDisplayItems('dashboards', dashboardItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.dashboards} onToggle={toggle} forceOpen={isSectionForceOpen('dashboards')} />
        )}
        {showSection('marketing', marketingItems) && (
          <Section id="marketing"  label="Marketing & Leads" icon={Megaphone}     items={getDisplayItems('marketing', marketingItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.marketing}  onToggle={toggle} forceOpen={isSectionForceOpen('marketing')} />
        )}
        {showSection('digital', digitalItems) && (
          <Section id="digital"    label="Digital Marketing" icon={ShareNetwork} items={getDisplayItems('digital', digitalItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.digital}    onToggle={toggle} forceOpen={isSectionForceOpen('digital')} />
        )}
        {showSection('crm', crmItems) && (
          <Section id="crm"        label="CRM & Sales"       icon={Users}         items={getDisplayItems('crm', crmItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.crm}        onToggle={toggle} forceOpen={isSectionForceOpen('crm')} />
        )}
        {showSection('products', productsItems) && (
          <Section id="products"   label="Products & Catalogue" icon={Tag}        items={getDisplayItems('products', productsItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.products}   onToggle={toggle} forceOpen={isSectionForceOpen('products')} />
        )}
        {showSection('finance', financeItems) && (
          <Section id="finance"    label="Finance"           icon={Receipt}       items={getDisplayItems('finance', financeItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.finance}    onToggle={toggle} forceOpen={isSectionForceOpen('finance')} />
        )}
        {showSection('purchase', purchaseItems) && (
          <Section id="purchase"   label="Purchase"          icon={ShoppingCart}  items={getDisplayItems('purchase', purchaseItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.purchase}   onToggle={toggle} forceOpen={isSectionForceOpen('purchase')} />
        )}
        {showSection('store', storeItems) && (
          <Section id="store"      label="Store & Delivery"  icon={Truck}         items={getDisplayItems('store', storeItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.store}      onToggle={toggle} forceOpen={isSectionForceOpen('store')} />
        )}
        {showSection('operations', operationsItems) && (
          <Section id="operations" label="Operations / Service" icon={Wrench}     items={getDisplayItems('operations', operationsItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.operations} onToggle={toggle} forceOpen={isSectionForceOpen('operations')} />
        )}
        {showSection('hr', hrItems) && (
          <Section id="hr"         label="HR & Team"         icon={IdentificationCard} items={getDisplayItems('hr', hrItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.hr}         onToggle={toggle} forceOpen={isSectionForceOpen('hr')} />
        )}
        {showSection('workspace', workspaceItems) && (
          <Section id="workspace"  label="Workspace"         icon={Briefcase}     items={getDisplayItems('workspace', workspaceItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.workspace}  onToggle={toggle} forceOpen={isSectionForceOpen('workspace')} />
        )}
        {showSection('admin', adminItems) && (
          <Section id="admin"      label="Admin Settings"    icon={ShieldCheck}   items={getDisplayItems('admin', adminItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.admin}      onToggle={toggle} forceOpen={isSectionForceOpen('admin')} />
        )}

        {access.reports && showSection('reports', reportsSectionItems) && (
          <Section id="reports"    label="Reports"           icon={ChartBar}      items={getDisplayItems('reports', reportsSectionItems)}
                   pathname={pathname} search={search} navigate={navigate} open={!!openSections.reports || reportsActive} onToggle={toggle} forceOpen={isSectionForceOpen('reports')} />
        )}
      </nav>

      {/* Logout — fixed at bottom (flex layout) */}
      <div className="p-2 border-t border-slate-700">
        <button data-testid="sidebar-logout-btn" onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-2 rounded-none bg-transparent text-slate-300 hover:bg-slate-800 hover:text-white transition-colors duration-150">
          <SignOut size={16} />
          <span className="font-medium text-xs">Logout</span>
        </button>
      </div>

      {/* Global Ctrl/Cmd+K palette — uses the SAME role-filtered sections */}
      <GlobalNavSearch open={paletteOpen} onClose={() => setPaletteOpen(false)} sections={sections} />
    </div>
  );
};

export default Sidebar;
